import React, { useEffect, useState } from "react";
import { Modal, Input, Button, Tooltip, message, Tag } from "antd";
import { SendOutlined } from "@ant-design/icons";
import { useSystemConfig } from "../../Hook";
import { useSendWhatsAppTextMessageMutation, useWhatsAppConnected } from "../../Services";
import type { RepairJob } from "../../Types/RepairJobTypes";

const QUICK_TEMPLATES: Array<{ label: string; text: string }> = [
  {
    label: "Status Update",
    text: "Hello {customer_name}, your {device} repair status has been updated to: {status}. Job #{job_number}. - {store_name}",
  },
  {
    label: "Ready for Pickup",
    text: "Hello {customer_name}, your {device} is now READY for pickup! Job #{job_number}. Please collect within 7 days. - {store_name}",
  },
  {
    label: "Payment Reminder",
    text: "Hello {customer_name}, a balance of {due_amount} is due for your {device} repair (Job #{job_number}). - {store_name}",
  },
];

const substitute = (template: string, job: RepairJob, store: any): string => {
  const map: Record<string, string> = {
    "{customer_name}":
      job.customer_name || job.temp_customer_name || "Customer",
    "{job_number}": job.job_number,
    "{device}": job.device_type,
    "{device_brand}": job.device_brand || "",
    "{device_model}": job.device_model || "",
    "{status}": (job.status || "").replace("_", " "),
    "{due_amount}": `Rs. ${Number(job.due_amount || 0).toFixed(2)}`,
    "{promised_date}": job.promised_date
      ? new Date(job.promised_date).toLocaleDateString()
      : "",
    "{store_name}": store.storeName || "",
    "{store_phone}": store.phone || "",
  };
  return template.replace(/\{\w+\}/g, (m) => map[m] ?? m);
};

interface SendWhatsAppMessageModalProps {
  open: boolean;
  job: RepairJob | null;
  onClose: () => void;
}

export const SendWhatsAppMessageModal: React.FC<
  SendWhatsAppMessageModalProps
> = ({ open, job, onClose }) => {
  const config = useSystemConfig();
  const [phone, setPhone] = useState("");
  const [messageText, setMessageText] = useState("");
  const [sending, setSending] = useState(false);
  const sendTextMessageMutation = useSendWhatsAppTextMessageMutation();
  const isWhatsAppConnected = true;//useWhatsAppConnected();

  useEffect(() => {
    if (open && job) {
      setPhone(job.customer_phone || job.temp_customer_phone || "");
      setMessageText(substitute(QUICK_TEMPLATES[0].text, job, config));
    }
  }, [open, job, config]);

  const handleSend = async () => {
    if (!phone.trim()) {
      message.warning("Enter the customer's WhatsApp number.");
      return;
    }
    if (!messageText.trim()) {
      message.warning("Enter the message text.");
      return;
    }

    setSending(true);
    try {
      const res = await sendTextMessageMutation.mutateAsync({
        phone: phone.trim(),
        message: messageText.trim(),
      });
      if (res?.success) {
        message.success("WhatsApp message sent!");
        onClose();
      } else {
        message.error(res?.message || "Failed to send WhatsApp message.");
      }
    } catch (error: any) {
      message.error(error?.message || "Failed to send WhatsApp message.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Send WhatsApp Message"
      width={460}
      destroyOnClose
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Tooltip
            title={!isWhatsAppConnected ? "WhatsApp gateway is not connected. Go to WhatsApp Chatbot settings to connect." : undefined}
          >
            <Button
              type="primary"
              icon={<SendOutlined />}
              loading={sending}
              onClick={handleSend}
              disabled={!isWhatsAppConnected}
              className="bg-green-600"
            >
              Send Message
            </Button>
          </Tooltip>
        </div>
      }
    >
      <div className="flex flex-col gap-3 text-sm">
        <div>
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
            Quick Templates
          </div>
          <div className="flex flex-wrap gap-1.5">
            {QUICK_TEMPLATES.map((t) => (
              <Tag
                key={t.label}
                color="blue"
                className="cursor-pointer select-none"
                onClick={() => setMessageText(substitute(t.text, job!, config))}
              >
                {t.label}
              </Tag>
            ))}
          </div>
        </div>

        <div>
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
            WhatsApp Number
          </div>
          <Input
            placeholder="e.g. 0712345678"
            prefix={<SendOutlined className="text-gray-400" />}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            allowClear
          />
        </div>

        <div>
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
            Message
          </div>
          <Input.TextArea
            rows={5}
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            placeholder="Type your custom message..."
          />
          <div className="text-[10px] text-gray-400 mt-1">
            Placeholders: {"{customer_name}"} {"{job_number}"} {"{device}"}{" "}
            {"{status}"} {"{due_amount}"} {"{promised_date}"} {"{store_name}"}
          </div>
        </div>
      </div>
    </Modal>
  );
};
